import React from 'react';
import {
  Flex,
  Box,
  SelectMenu,
  InputLabel,
  Checkbox,
  InputDescription,
  OptionType,
} from '@fullstory/components';
import { FormattedMessage } from 'react-intl';
import { intl } from '@fullstory/intl';
import { REQUIRED_FIELD_STRING, typeSupportsFeatureCard } from '../../utils';
import { ObjectType } from '@fullstory/proto/fs/services/dx-objects';
import { SpaceActionType } from '../../types';
import { useFlags } from '@fullstory/react-hooks';

export interface SpaceAndSectionSelectProps {
  objectType: ObjectType;
  selectedSpace: OptionType<string> | null;
  onSpaceSelect: (option: OptionType<string> | null) => void;
  spaceOptions: OptionType<string>[];
  selectedSection: OptionType<string> | null;
  onSectionSelect: (option: OptionType<string> | null) => void;
  sectionRequired: boolean;
  sectionOptions: OptionType<string>[] | null;
  keepReference: boolean;
  onKeepReferenceChange: (keepReference: boolean) => void;
  mode: SpaceActionType;
  disableSpaceSelect: boolean;
  touchedSpaceSelect: boolean;
  setTouchedSpaceSelect: (touched: boolean) => void;
  touchedSectionSelect: boolean;
  variant: React.ComponentProps<typeof SelectMenu>['variant'];
}

export const SpaceAndSectionSelect: React.FC<SpaceAndSectionSelectProps> = ({
  objectType,
  spaceOptions,
  selectedSpace,
  onSpaceSelect,
  sectionOptions,
  selectedSection,
  onSectionSelect,
  sectionRequired,
  keepReference,
  onKeepReferenceChange,
  mode,
  disableSpaceSelect,
  touchedSpaceSelect,
  setTouchedSpaceSelect,
  touchedSectionSelect,
  variant,
}) => {
  const flags = useFlags();

  return (
    <Flex column gap={24} alignItems="flex-start">
      {mode !== 'feature' && (
        <Box width={1}>
          <InputLabel htmlFor="space-select">
            <FormattedMessage defaultMessage="Space" id="0ah2ud" />
          </InputLabel>
          <SelectMenu
            id="space-select"
            testId="space-select"
            filterable
            onChange={opt => {
              setTouchedSpaceSelect(true);
              onSpaceSelect(opt);
            }}
            selected={selectedSpace}
            options={spaceOptions ?? []}
            variant={variant}
            placeholder={intl.formatMessage({ defaultMessage: 'Select a space', id: 'R10+OI' })}
            fullWidth
            menuHeight={385}
            disabled={disableSpaceSelect}
          />
          {!selectedSpace && touchedSpaceSelect && (
            <InputDescription invalid description={REQUIRED_FIELD_STRING} />
          )}
        </Box>
      )}

      {typeSupportsFeatureCard(objectType, flags) && (
        <Box width={1}>
          <InputLabel
            required={sectionRequired && mode === 'add'}
            optional={!sectionRequired}
            disabled={!sectionOptions?.length}
            htmlFor="section-select"
          >
            <FormattedMessage defaultMessage="Feature this item" id="8cAn2Z" />
          </InputLabel>
          <SelectMenu
            id="section-select"
            filterable
            onChange={onSectionSelect}
            selected={selectedSection}
            options={sectionOptions ?? []}
            variant={variant}
            placeholder={intl.formatMessage({
              defaultMessage: 'Select a section',
              id: '7mNe0C',
            })}
            fullWidth
            menuHeight={385}
            clearable
            disabled={!sectionOptions?.length || !selectedSpace}
          />
          {touchedSectionSelect && <InputDescription invalid description={REQUIRED_FIELD_STRING} />}
        </Box>
      )}

      {mode === 'transfer' && (
        <Checkbox
          label={intl.formatMessage({
            defaultMessage: 'Keep in the current space as a link.',
            id: '0n+PD9',
          })}
          checked={keepReference}
          onChange={onKeepReferenceChange}
        />
      )}
    </Flex>
  );
};
